import React, { useState } from 'react';
import channelService from '../services/channelService';
import type {
  CreateChannelDto,
  OutputFormat,
  TranscodePreset,
  OutputResolution,
  LogoPosition,
  HlsBitrateConfig,
} from '../services/channelService';

interface ChannelWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ChannelWizard: React.FC<ChannelWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [slateFile, setSlateFile] = useState<File | null>(null);
  const [slatePreview, setSlatePreview] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateChannelDto>({
    name: '',
    description: '',
    output_format: 'hls',
    transcode_preset: 'faster',
    output_resolution: '720p',
    hls_segment_duration: 4,  // Default to 4 seconds
    hls_playlist_length: 10,
    dvr_window: 0,
    low_latency_mode: false,
    rtmp_url: '',
    rtmp_key: '',
    logo_position: 'top-right',
    show_now_playing: false,
    show_up_next: false,
    ad_interval: 300,         // 5 minutes default
    ad_duration: 30,          // 30 seconds default
    hls_bitrate_config: '720p',
  });

  // Validation
  const validateStep = (currentStep: number): boolean => {
    setError(null);

    if (currentStep === 1) {
      if (!formData.name.trim()) {
        setError('Channel name is required');
        return false;
      }
      if (formData.name.trim().length < 3) {
        setError('Channel name must be at least 3 characters');
        return false;
      }
    }

    if (currentStep === 2) {
      if (formData.output_format === 'rtmp' || formData.output_format === 'both') {
        if (!formData.rtmp_url?.trim()) {
          setError('RTMP URL is required for RTMP output');
          return false;
        }
      }
    }

    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  // Handle previous step
  const handlePrevious = () => {
    setError(null);
    setStep(step - 1);
  };

  // Handle logo file selection
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only PNG, JPEG, SVG, and WebP are allowed');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB');
      return;
    }

    setLogoFile(file);
    setError(null);

    // Generate preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  // Handle slate file selection
  const handleSlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type (video files)
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only MP4, WebM, MOV, and AVI are allowed');
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB');
      return;
    }

    setSlateFile(file);
    setError(null);

    // Generate video thumbnail preview
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      video.currentTime = 1; // Seek to 1 second for thumbnail
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        setSlatePreview(canvas.toDataURL('image/jpeg'));
      }
      URL.revokeObjectURL(video.src);
    };
    video.src = URL.createObjectURL(file);
  };

  // Remove slate
  const handleRemoveSlate = () => {
    setSlateFile(null);
    setSlatePreview(null);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep(step)) return;

    try {
      setLoading(true);
      setError(null);

      // Clean up form data based on output format
      const submitData: CreateChannelDto = { ...formData };

      if (submitData.output_format === 'hls') {
        // Remove RTMP fields if not using RTMP
        delete submitData.rtmp_url;
        delete submitData.rtmp_key;
      }

      // Create channel
      const channel = await channelService.createChannel(submitData);

      // Upload logo if selected
      if (logoFile && channel?.id) {
        try {
          await channelService.uploadLogo(channel.id, logoFile);
        } catch (logoErr: any) {
          console.error('Logo upload failed:', logoErr);
          // Don't fail channel creation if logo upload fails
          // User can upload it later
        }
      }

      // Upload slate if selected
      if (slateFile && channel?.id) {
        try {
          await channelService.uploadSlate(channel.id, slateFile);
        } catch (slateErr: any) {
          console.error('Slate upload failed:', slateErr);
          // Don't fail channel creation if slate upload fails
        }
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create channel');
      console.error('Error creating channel:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setStep(1);
    setError(null);
    setLogoFile(null);
    setLogoPreview(null);
    setSlateFile(null);
    setSlatePreview(null);
    setFormData({
      name: '',
      description: '',
      output_format: 'hls',
      transcode_preset: 'faster',
      output_resolution: '720p',
      hls_segment_duration: 4,
      hls_playlist_length: 10,
      dvr_window: 0,
      low_latency_mode: false,
      rtmp_url: '',
      rtmp_key: '',
      logo_position: 'top-right',
      show_now_playing: false,
      show_up_next: false,
      ad_interval: 300,
      ad_duration: 30,
      hls_bitrate_config: '720p',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create New Channel</h2>
            <p className="text-sm text-gray-600 mt-1">Step {step} of 4</p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`flex-1 h-2 rounded-full ${
                  i <= step ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Form content */}
        <div className="px-6 py-6">
          {/* Step 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Sports Channel 24/7"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Choose a descriptive name for your channel
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional description of your channel"
                />
              </div>
            </div>
          )}

          {/* Step 2: Output Settings */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Output Format <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(['hls', 'rtmp', 'both'] as OutputFormat[]).map((format) => (
                    <button
                      key={format}
                      onClick={() => setFormData({ ...formData, output_format: format })}
                      className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-colors ${
                        formData.output_format === format
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      }`}
                    >
                      {format === 'hls' && 'HLS Only'}
                      {format === 'rtmp' && 'RTMP Only'}
                      {format === 'both' && 'HLS + RTMP'}
                    </button>
                  ))}
                </div>
              </div>

              {(formData.output_format === 'rtmp' || formData.output_format === 'both') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RTMP URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.rtmp_url}
                      onChange={(e) =>
                        setFormData({ ...formData, rtmp_url: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="rtmp://a.rtmp.youtube.com/live2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      RTMP Stream Key
                    </label>
                    <input
                      type="password"
                      value={formData.rtmp_key}
                      onChange={(e) =>
                        setFormData({ ...formData, rtmp_key: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Your stream key"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HLS Bitrate Configuration
                </label>
                <select
                  value={formData.hls_bitrate_config}
                  onChange={(e) => {
                    const newConfig = e.target.value as HlsBitrateConfig;
                    setFormData({
                      ...formData,
                      hls_bitrate_config: newConfig,
                      // Lock segment duration to 4 for Amagi mode
                      hls_segment_duration: newConfig === 'amagi' ? 4 : formData.hls_segment_duration,
                    });
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="amagi">Amagi Config (Multi-bitrate ABR)</option>
                  <option value="720p">720p Only</option>
                  <option value="1080p">1080p Only</option>
                  <option value="720p_1080p">720p + 1080p (ABR)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Amagi: 240p/360p/480p/720p/1080p adaptive streaming with 4-sec segments
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Output Resolution
                </label>
                <select
                  value={formData.output_resolution}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      output_resolution: e.target.value as OutputResolution,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="720p">720p (HD)</option>
                  <option value="1080p">1080p (Full HD)</option>
                  <option value="adaptive">Adaptive (Multiple)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transcode Preset
                </label>
                <select
                  value={formData.transcode_preset}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      transcode_preset: e.target.value as TranscodePreset,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ultrafast">Ultrafast (Lowest quality, fastest)</option>
                  <option value="superfast">Superfast</option>
                  <option value="faster">Faster (Recommended)</option>
                  <option value="fast">Fast</option>
                  <option value="medium">Medium (Best quality, slowest)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Faster presets use less CPU but may reduce quality
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Advanced Settings */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HLS Segment Duration (seconds)
                </label>
                <input
                  type="number"
                  min="2"
                  max="10"
                  value={formData.hls_segment_duration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hls_segment_duration: parseInt(e.target.value),
                    })
                  }
                  disabled={formData.hls_bitrate_config === 'amagi'}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    formData.hls_bitrate_config === 'amagi' ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {formData.hls_bitrate_config === 'amagi'
                    ? 'Fixed at 4 seconds for Amagi mode (required for proper synchronization)'
                    : 'Default: 4 seconds. Lower values reduce latency but increase CPU usage'}
                </p>
              </div>

              {/* Ad Break Settings */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Ad Break Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad Interval (seconds)
                    </label>
                    <input
                      type="number"
                      min="60"
                      max="3600"
                      step="30"
                      value={formData.ad_interval}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          ad_interval: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Time between ad breaks ({Math.floor((formData.ad_interval || 300) / 60)} min)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ad Duration (seconds)
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="180"
                      step="5"
                      value={formData.ad_duration}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          ad_duration: parseInt(e.target.value),
                        })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Duration of each ad break
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HLS Playlist Length (segments)
                </label>
                <input
                  type="number"
                  min="3"
                  max="20"
                  value={formData.hls_playlist_length}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      hls_playlist_length: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DVR Window (seconds)
                </label>
                <input
                  type="number"
                  min="0"
                  max="7200"
                  step="60"
                  value={formData.dvr_window}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      dvr_window: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">
                  0 = disabled. Enable DVR to allow viewers to rewind the stream
                </p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="low_latency_mode"
                  checked={formData.low_latency_mode}
                  onChange={(e) =>
                    setFormData({ ...formData, low_latency_mode: e.target.checked })
                  }
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="low_latency_mode" className="text-sm font-medium text-gray-700">
                  Enable Low Latency Mode
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-7">
                Reduces latency but may require more bandwidth
              </p>

              {/* Logo Upload */}
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Channel Logo (Optional)
                </label>
                {!logoPreview ? (
                  <div>
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg cursor-pointer transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      Choose Logo
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      PNG, JPEG, SVG, or WebP. Max 5MB. Will be displayed in top-right corner.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="relative w-32 h-32 bg-gray-100 rounded-lg border-2 border-gray-300 overflow-hidden">
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{logoFile?.name}</p>
                      <p className="text-xs text-gray-500">
                        {((logoFile?.size || 0) / 1024).toFixed(1)} KB
                      </p>
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* Logo Position */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo Position
                  </label>
                  <select
                    value={formData.logo_position}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        logo_position: e.target.value as LogoPosition,
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="top-left">Top Left</option>
                    <option value="top-right">Top Right</option>
                    <option value="bottom-left">Bottom Left</option>
                    <option value="bottom-right">Bottom Right</option>
                  </select>
                </div>
              </div>

              {/* Slate Video Upload */}
              <div className="pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Slate Video (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Upload a custom slate video to show during ad breaks. If not provided, a default "We'll be right back" screen will be used.
                </p>
                {!slatePreview ? (
                  <div>
                    <input
                      type="file"
                      id="slate-upload"
                      accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                      onChange={handleSlateChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="slate-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg cursor-pointer transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      Choose Slate Video
                    </label>
                    <p className="mt-1 text-xs text-gray-500">
                      MP4, WebM, MOV, or AVI. Max 50MB.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="relative w-32 h-20 bg-gray-100 rounded-lg border-2 border-gray-300 overflow-hidden">
                      <img
                        src={slatePreview}
                        alt="Slate preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{slateFile?.name}</p>
                      <p className="text-xs text-gray-500">
                        {((slateFile?.size || 0) / (1024 * 1024)).toFixed(1)} MB
                      </p>
                      <button
                        type="button"
                        onClick={handleRemoveSlate}
                        className="mt-2 text-sm text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-gray-900">Channel Summary</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-medium text-gray-900">{formData.name}</span>
                  </div>

                  {formData.description && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Description:</span>
                      <span className="font-medium text-gray-900 text-right max-w-xs">
                        {formData.description}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span className="text-gray-600">Output Format:</span>
                    <span className="font-medium text-gray-900">
                      {formData.output_format === 'hls' && 'HLS Only'}
                      {formData.output_format === 'rtmp' && 'RTMP Only'}
                      {formData.output_format === 'both' && 'HLS + RTMP'}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Resolution:</span>
                    <span className="font-medium text-gray-900">
                      {formData.output_resolution.toUpperCase()}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Transcode Preset:</span>
                    <span className="font-medium text-gray-900">{formData.transcode_preset}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Segment Duration:</span>
                    <span className="font-medium text-gray-900">
                      {formData.hls_segment_duration}s
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">HLS Bitrate:</span>
                    <span className="font-medium text-gray-900">
                      {formData.hls_bitrate_config === 'amagi' && 'Amagi Config'}
                      {formData.hls_bitrate_config === '720p' && '720p Only'}
                      {formData.hls_bitrate_config === '1080p' && '1080p Only'}
                      {formData.hls_bitrate_config === '720p_1080p' && '720p + 1080p'}
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Ad Interval:</span>
                    <span className="font-medium text-gray-900">
                      {Math.floor((formData.ad_interval || 300) / 60)} min
                    </span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-gray-600">Ad Duration:</span>
                    <span className="font-medium text-gray-900">
                      {formData.ad_duration}s
                    </span>
                  </div>

                  {formData.dvr_window && formData.dvr_window > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">DVR Window:</span>
                      <span className="font-medium text-gray-900">
                        {Math.floor(formData.dvr_window / 60)} minutes
                      </span>
                    </div>
                  )}

                  {formData.low_latency_mode && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Low Latency:</span>
                      <span className="font-medium text-green-600">Enabled</span>
                    </div>
                  )}

                  {logoFile && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Logo:</span>
                      <span className="font-medium text-gray-900">{logoFile.name}</span>
                    </div>
                  )}

                  {logoFile && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Logo Position:</span>
                      <span className="font-medium text-gray-900 capitalize">
                        {formData.logo_position?.replace('-', ' ')}
                      </span>
                    </div>
                  )}

                  {slateFile && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Custom Slate:</span>
                      <span className="font-medium text-gray-900">{slateFile.name}</span>
                    </div>
                  )}

                  {!slateFile && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Slate:</span>
                      <span className="font-medium text-gray-500">Default (We'll be right back)</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> The channel will be created in a stopped state. You can start it from the channels page once you've configured your schedule.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={step === 1 ? handleClose : handlePrevious}
            disabled={loading}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {step === 1 ? 'Cancel' : 'Previous'}
          </button>

          {step < 4 ? (
            <button
              onClick={handleNext}
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Create Channel
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChannelWizard;
